import {
  Header,
  Icon,
  Text,
  Modal,
  Button,
  Box,
  LinkButton,
  Span,
  SingleDatePicker,
  Input,
  useTheme,
  IconButton,
  Gap,
  Tooltip,
} from '@salecycle/ui-component-library';
import { Campaign } from 'entities/campaign';
import { Helpers } from 'entities/lifecycle';
import { FC, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import moment, { Moment } from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  resetGradientBarVariation,
  usePreferencesState,
  usePreferencesDispatch,
  updateGradientBarVariation,
} from 'core/preferences/usePreferencesContext';
import useUserDetails from 'core/ui/utils/useUserDetails';
import { basisTranslationMap } from 'entities/consent';
import { Selectors as Router } from 'router';
import Site, { UnsubscribeSettings } from 'sections/client-management/entities/site';
import { Selectors as SiteSelectors } from 'sections/client-management/services/sites';
import { Selectors as EditorSelectors } from 'sections/campaigns/state/editor';
import { Actions as CampaignActions, Selectors as CampaignSelectors } from '../../../state/campaigns';
import { Actions as EditorActions } from '../../../state/editor';
import { Selectors as RulesetSplitTestSelectors } from '../../../state/rulesetSplitTests';
import { State } from 'store';
import { IPWhitelist } from '.';
import StateBadge from '../../components/StateBadge';
import CampaignActionsButton from '../../components/CampaignActionsButton';
import { getGradientVariation } from 'entities/lifecycle/activityState';
import { UNSUBSCRIBE_ITEMS } from 'core/constants/UnsubscribeConstants';

const StyledSidebar = styled.div`
  z-index: 1;
  box-shadow: ${({ theme }) => theme.vars.boxShadow.overlayMenu};
  padding: 4.5rem 4rem;
`;

const SidebarHeaderContainer = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: 1fr auto;
  margin-bottom: 2.5rem;
  margin-left: -6px;
`;

const HeaderWrapper = styled.div`
  margin-bottom: -1.5rem;
`;

const SidebarList = styled.ul`
  padding: 0;
  margin: 0;
  list-style-type: none;
`;

const SidebarListItem = styled.li`
  margin-bottom: 1rem;
`;

export const SidebarLabel = styled.div`
  display: inline-block;
  margin-right: 1rem;
`;

const SidebarDates = styled.div<{ hasDate: boolean }>`
  display: inline-flex;
  align-items: center;
  margin-top: ${({ hasDate }) => (hasDate ? '1rem' : null)};
`;

const TimezoneOffset = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 1rem;
`;

const LastEdited = styled.div`
  margin-top: 3.5rem;
  border-top: 1px dashed ${({ theme }) => theme.vars.newColor.darkGS3};
  padding-top: 2rem;
  font-style: italic;
`;

const LinkIcon = styled.span`
  display: inline-block;
  margin-left: 1.5rem;
`;

type ICampaignTimeType = 'startTime' | 'endTime';

interface IOwnProps {
  siteId: string;
  hasUnsavedChanges: boolean;
  hasMissingFields: boolean;
}

interface IStateProps {
  campaign: Campaign;
  originalCampaign: Campaign;
  campaignId: string;
  isInActiveSplitTest: boolean;
  site: Site;
  rulesetIds: string[];
}

interface IDispatchProps {
  editCampaign: (campaignId: string, changes: Campaign) => void;
  update?: (campaignId: string, changes: Campaign) => void;
  save: () => void;
}

export type Props = IOwnProps & IStateProps & IDispatchProps;

function changeInLifecycle(a: Campaign, b: Campaign): boolean {
  const result = a && b && Helpers.getActivityState(a) !== Helpers.getActivityState(b);
  return result;
}

export const Sidebar: FC<Props> = props => {
  const { t } = useTranslation(['integrations', 'campaigns', 'marketingPermissions', 'common']);
  const theme = useTheme();
  const { timezone } = usePreferencesState();
  const dispatch = usePreferencesDispatch();
  const gradientVariation = getGradientVariation(props.campaign);

  const {
    push,
    location: { pathname },
  } = useHistory();
  const { campaignId } = useParams<{ campaignId: string }>();
  const baseUrl = pathname.replace('/campaigns/campaigns/' + campaignId, '');

  const [isWhitelistOpen, setIsWhitelistOpen] = useState(false);
  const [isClosePromptOpen, setIsClosePromptOpen] = useState(false);
  const [isStartDateFocused, setIsStartDateFocused] = useState(false);
  const [isEndDateFocused, setIsEndDateFocused] = useState(false);

  // Get the user details for the Last Edited by text.
  const { userDetails, status: userDetailsStatus } = useUserDetails(props.campaign.audit.lastUpdatedBy);

  useEffect(() => {
    updateGradientBarVariation(dispatch, gradientVariation);

    return () => resetGradientBarVariation(dispatch);
  }, [dispatch, gradientVariation, props.campaign.state]);

  if (!props.campaign) {
    return null;
  }

  const timezoneOffset = `UTC ${moment()
    .tz(timezone)
    .format('Z')
    .replace(':00', '')}`;

  const startTimeMoment = props.campaign.startTime ? moment(props.campaign.startTime).tz(timezone) : null;
  const endTimeMoment = props.campaign.endTime ? moment(props.campaign.endTime).tz(timezone) : null;
  const sameDay = startTimeMoment && endTimeMoment && startTimeMoment.isSame(endTimeMoment, 'date');

  const onChangeTime = (date: Moment, type: ICampaignTimeType) =>
    props.editCampaign(props.campaignId, {
      [type]: date ? date.valueOf() : null,
    });

  const onBackLinkClick = () => {
    if (props.hasUnsavedChanges) {
      setIsClosePromptOpen(true);
    } else {
      push(`/sites/${props.siteId}/campaigns/campaigns`);
    }
  };

  const backToCampaigns = () => push(`/sites/${props.siteId}/campaigns/campaigns`);

  const onModalSaveClick = () => {
    props.save();
    backToCampaigns();
  };

  const updateCampaignName = (name: string) => {
    props.update(props.campaignId, { name });
  };

  const unsubscribeValue = (unsubscribeSettings: UnsubscribeSettings) => {
    if (unsubscribeSettings?.globalUnsubscribe !== null && unsubscribeSettings?.globalUnsubscribe !== undefined) {
      return t(`common:${UNSUBSCRIBE_ITEMS.find(item => item.id === 'GlobalUnsubscribe').name}`);
    }
    if (unsubscribeSettings?.unsubscribeURL !== null && unsubscribeSettings?.unsubscribeURL !== undefined) {
      return unsubscribeSettings.unsubscribeURL;
    }
    return t(`common:${UNSUBSCRIBE_ITEMS.find(item => item.id === 'GlobalUnsubscribe').name}`);
  };

  return (
    <StyledSidebar data-testid="Sidebar">
      <Box margin="0 0 3rem 0">
        <LinkButton ariaLabel={t('campaigns:editor.sidebar.back')} onClick={onBackLinkClick}>
          <Icon icon="angle-double-left" /> {t('campaigns:editor.sidebar.back')}
        </LinkButton>
      </Box>

      <SidebarHeaderContainer>
        <HeaderWrapper>
          <Header editable onChange={updateCampaignName} heading={props.campaign.name}>
            {props.isInActiveSplitTest && (
              <>
                <Icon icon="exclamation-triangle" color="red" size="large" />
                &nbsp;&nbsp;&nbsp;
                <Text size={24} weight="bold">
                  Active Split Test
                </Text>
              </>
            )}
          </Header>
        </HeaderWrapper>
      </SidebarHeaderContainer>

      <SidebarList>
        <SidebarListItem data-testid="Objective">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:editor.sidebar.items.objective')}:</Span>
          </SidebarLabel>

          {props.campaign.objective}
        </SidebarListItem>

        <SidebarListItem data-testid="Feature">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:editor.sidebar.items.feature')}:</Span>
          </SidebarLabel>
          {props.campaign.type}
        </SidebarListItem>

        {props.rulesetIds && (
          <SidebarListItem data-testid="Rulesets">
            <SidebarLabel>
              <Span bold={true}>{t('campaigns:editor.sidebar.items.rulesets')}: </Span>
            </SidebarLabel>

            {props.rulesetIds.length}
          </SidebarListItem>
        )}

        {props.site && props.campaign?.type === 'Cart Abandonment' && (
          <SidebarListItem data-testid="Unsubscribe">
            <SidebarLabel>
              <Span bold={true}>{t('common:client.siteForm.unsubscribe.label')}: </Span>
            </SidebarLabel>

            <a href={`${baseUrl}/settings/settings`} target="_self" rel="noopener noreferrer">
              {unsubscribeValue(props.site?.unsubscribeSettings)}
              <LinkIcon>
                <Icon icon="external-link-square" color={theme.vars.newColor.darkGS2} />
              </LinkIcon>
            </a>
          </SidebarListItem>
        )}

        {props.site?.marketingPermissions && (
          <SidebarListItem data-testid="MPSDetails">
            <SidebarLabel>
              <Span bold={true}>{t('campaigns:editor.sidebar.items.mps')}: </Span>
            </SidebarLabel>

            <a href={`${baseUrl}/settings/marketing-permissions`} target="_blank" rel="noopener noreferrer">
              {t(
                `marketingPermissions:basisDescriptions.${basisTranslationMap[
                  props.site.marketingPermissions.consentBasis[0]
                ] ?? 'disabledBasis'}.label`,
              )}

              <LinkIcon>
                <Icon icon="external-link-square" color={theme.vars.newColor.darkGS2} />
              </LinkIcon>
            </a>
          </SidebarListItem>
        )}

        <SidebarListItem data-testid="StartDate">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:editor.sidebar.items.startDate')}:</Span>
          </SidebarLabel>

          <SidebarDates hasDate={!!startTimeMoment}>
            <SingleDatePicker
              ariaLabel={t('campaigns:dates.start.label')}
              date={startTimeMoment}
              focused={isStartDateFocused}
              isFutureDatesOnly={true}
              isMini={true}
              onDateChange={(selectedDate: Moment) =>
                onChangeTime(moment.tz(`${selectedDate.format('YYYY-MM-DD')}T00:00`, timezone), 'startTime')
              }
              onFocusChange={({ focused }) => setIsStartDateFocused(focused)}
              placeholder={t('campaigns:dates.start.datePlaceholder')}
              spacingBelow={false}
            />

            {startTimeMoment && (
              <>
                <Input
                  ariaLabel={t('campaigns:dates.start.timeLabel')}
                  type="time"
                  spacingBelow={false}
                  value={startTimeMoment.format('HH:mm')}
                  min="00:00"
                  max={sameDay ? endTimeMoment.format('HH:mm') : null}
                  onChange={selectedTime =>
                    onChangeTime(
                      moment.tz(`${startTimeMoment.format('YYYY-MM-DD')}T${selectedTime}`, timezone),
                      'startTime',
                    )
                  }
                  isMini={true}
                />

                <IconButton
                  ariaLabel={t('campaigns:dates.start.clear')}
                  icon="times"
                  onClick={e => {
                    e.preventDefault();
                    onChangeTime(null, 'startTime');
                  }}
                />

                <TimezoneOffset data-testid="TimezoneOffset">{timezoneOffset}</TimezoneOffset>
              </>
            )}
          </SidebarDates>
        </SidebarListItem>

        <SidebarListItem data-testid="EndDate">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:editor.sidebar.items.endDate')}:</Span>
          </SidebarLabel>

          <SidebarDates hasDate={!!endTimeMoment}>
            <SingleDatePicker
              ariaLabel={t('campaigns:dates.end.label')}
              date={endTimeMoment}
              focused={isEndDateFocused}
              isFutureDatesOnly={true}
              isMini={true}
              onDateChange={(selectedDate: Moment) =>
                onChangeTime(moment.tz(`${selectedDate.format('YYYY-MM-DD')}T23:00`, timezone), 'endTime')
              }
              onFocusChange={({ focused }) => setIsEndDateFocused(focused)}
              placeholder={t('campaigns:dates.end.datePlaceholder')}
              spacingBelow={false}
            />

            {endTimeMoment && (
              <>
                <Input
                  ariaLabel={t('campaigns:dates.end.timeLabel')}
                  type="time"
                  spacingBelow={false}
                  value={endTimeMoment.format('HH:mm')}
                  min={sameDay ? startTimeMoment.format('HH:mm') : null}
                  max="23:59"
                  onChange={selectedTime =>
                    onChangeTime(
                      moment.tz(`${endTimeMoment.format('YYYY-MM-DD')}T${selectedTime}`, timezone),
                      'endTime',
                    )
                  }
                  isMini={true}
                />

                <IconButton
                  ariaLabel={t('campaigns:dates.end.clear')}
                  icon="times"
                  onClick={e => {
                    e.preventDefault();
                    onChangeTime(null, 'endTime');
                  }}
                />

                <TimezoneOffset data-testid="TimezoneOffset">{timezoneOffset}</TimezoneOffset>
              </>
            )}
          </SidebarDates>
        </SidebarListItem>

        <SidebarListItem data-testid="Whitelist">
          <SidebarLabel>
            <Span>
              <Tooltip title={t('campaigns:editor.whitelist.tooltipDescription')} position="right">
                <Span>
                  {t('campaigns:editor.whitelist.label')}&nbsp;&nbsp;
                  <Icon icon="question-circle" />
                </Span>
              </Tooltip>
            </Span>
          </SidebarLabel>

          <Box padding="1rem 0 0">
            <Button variation="secondary" onClick={() => setIsWhitelistOpen(true)}>
              {t('campaigns:editor.whitelist.buttonLabel')}
            </Button>
          </Box>
        </SidebarListItem>

        <SidebarListItem data-testid="Status">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:editor.sidebar.items.status')}:</Span>
          </SidebarLabel>

          <Box padding="1rem 0 0">
            {changeInLifecycle(props.campaign, props.originalCampaign) ? (
              <>
                <StateBadge lifeCycle={props.originalCampaign} />

                <Box padding="1rem">
                  <Icon icon="angle-double-right" />
                </Box>
              </>
            ) : null}
            <StateBadge lifeCycle={props.campaign} />
            <Gap right={10} />
            <CampaignActionsButton
              campaignId={props.campaignId}
              campaign={props.campaign}
              siteId={props.siteId}
              updateLocalOnly={true}
            />
          </Box>
        </SidebarListItem>
        <SidebarListItem data-testid="Site">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:editor.sidebar.items.site')}: </Span>
          </SidebarLabel>
          {props.site.name}
        </SidebarListItem>
      </SidebarList>

      {userDetailsStatus === 'resolved' && (
        <LastEdited data-testid="LastEdited">
          <Span>
            {t('campaigns:editor.sidebar.lastEdited', {
              name: userDetails.name,
              timeago: moment(props.campaign.audit.lastUpdatedAt)
                .tz(timezone)
                .fromNow(),
            })}
          </Span>
        </LastEdited>
      )}

      <Modal
        heading={t('campaigns:editor.whitelist.modalHeading')}
        isOpen={isWhitelistOpen}
        variation="clean"
        onOkButtonClicked={() => setIsWhitelistOpen(false)}
        onCloseButtonClicked={() => setIsWhitelistOpen(false)}
        okButtonLabel="Close"
      >
        <IPWhitelist />
      </Modal>

      <Modal
        heading={t('campaigns:editor.sidebar.discard.modal.heading')}
        isOpen={isClosePromptOpen}
        variation="clean"
        okButtonLabel={t('campaigns:editor.sidebar.discard.modal.okButtonLabel')}
        onOkButtonClicked={backToCampaigns}
        cancelButtonLabel={
          props.hasMissingFields
            ? t('campaigns:editor.sidebar.discard.modal.cancelButtonLabel.missingFields')
            : t('campaigns:editor.sidebar.discard.modal.cancelButtonLabel.noMissingFields')
        }
        onCancelButtonClicked={props.hasMissingFields ? () => setIsClosePromptOpen(false) : onModalSaveClick}
        onCloseButtonClicked={() => setIsClosePromptOpen(false)}
      >
        <p>{t('campaigns:editor.sidebar.discard.modal.message')}</p>
      </Modal>
    </StyledSidebar>
  );
};

const mapState = (state: State): IStateProps => {
  const campaignId = Router.getCampaignId(state);
  const campaign = CampaignSelectors.getUpdatedEntity(state, campaignId);
  const siteId = Router.getSiteId(state);
  const site = SiteSelectors.getUpdatedEntity(state, siteId);

  return {
    originalCampaign: CampaignSelectors.getEntity(state, campaignId),
    campaign,
    campaignId,
    isInActiveSplitTest: RulesetSplitTestSelectors.getActiveSplitTestsForCampaign(state, campaignId).length > 0,
    site,
    rulesetIds: EditorSelectors.getRulesetIds(state),
  };
};

const actionCreators: IDispatchProps = {
  editCampaign: CampaignActions.updateLocal,
  update: CampaignActions.updateLocal,
  save: EditorActions.saveChanges,
};

export default connect(mapState, actionCreators)(Sidebar);
